/**
 * Document ID Generation Utilities
 * 
 * Provides consistent human-readable ID generation for Firestore documents.
 * 
 * Entity Document ID Formats:
 * - Opportunities: OPP-{number} (e.g., OPP-1, OPP-54, OPP-2435)
 * - Tasks: TSK-{number} (e.g., TSK-3, TSK-984, TSK-47384)
 * - Users (manual): USR-{number} (e.g., USR-43, USR-8754)
 * 
 * Log Document ID Formats:
 * - {level}_{entityType}_{entityId}_{action}_{timestamp}
 */

/**
 * Log Levels for structured logging
 */
export const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
};

/**
 * Generate document ID for opportunities
 * @param {number} sequentialNumber - Sequential number from counter
 * @returns {string} Format: OPP-{number} (e.g., OPP-1, OPP-54, OPP-2435)
 */
export function generateOpportunityId(sequentialNumber) {
  return `OPP-${sequentialNumber}`;
}

/**
 * Generate document ID for tasks
 * @param {number} sequentialNumber - Sequential number from counter
 * @returns {string} Format: TSK-{number} (e.g., TSK-3, TSK-984, TSK-47384)
 */
export function generateTaskId(sequentialNumber) {
  return `TSK-${sequentialNumber}`;
}

/**
 * Generate document ID for manually created users
 * @param {number} sequentialNumber - Sequential number from counter
 * @returns {string} Format: USR-{number} (e.g., USR-43, USR-8754)
 */
export function generateUserId(sequentialNumber) {
  return `USR-${sequentialNumber}`;
}

/**
 * Generate ID for log documents
 * @param {string} entityType - Type of entity (customer, contact, opp, task, user)
 * @param {string} entityId - ID of the entity being logged
 * @param {string} action - Action performed (created, updated, deleted)
 * @param {string} level - Log level (debug, info, warn, error) - defaults to 'info'
 * @returns {string} Format: {level}_{entityType}_{entityId}_{action}_{timestamp}
 */
export function generateLogId(entityType, entityId, action, level = LOG_LEVELS.INFO) {
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  const sanitizedAction = (action || 'updated').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  const sanitizedLevel = (level || LOG_LEVELS.INFO).toLowerCase();
  // Normalize entityType to lowercase for consistency
  const normalizedEntityType = (entityType || '').toLowerCase();
  // Normalize entityId to handle potential special characters
  const sanitizedEntityId = (entityId || 'unknown').toString().replace(/[^a-zA-Z0-9-_]/g, '_');
  return `${sanitizedLevel}_${normalizedEntityType}_${sanitizedEntityId}_${sanitizedAction}_${timestamp}`;
}

/**
 * Generate ID for email templates
 * @param {string} name - Template name
 * @returns {string} Format: template_{sanitizedName}_{timestamp}
 */
export function generateTemplateId(name) {
  const sanitized = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .substring(0, 50);
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  return `template_${sanitized}_${timestamp}`;
}

/**
 * Generate ID for email segments
 * @param {string} name - Segment name
 * @returns {string} Format: segment_{sanitizedName}_{timestamp}
 */
export function generateSegmentId(name) {
  const sanitized = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 50);
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  return `segment_${sanitized}_${timestamp}`;
}

/**
 * Generate ID for email events
 * @param {string} campaignId - Campaign ID
 * @param {string} eventType - Event type (sent, delivered, opened, clicked, bounced, unsubscribed)
 * @param {string} recipientEmail - Recipient email address
 * @returns {string} Format: {campaignId}_{eventType}_{emailHash}_{timestamp}
 */
export function generateEmailEventId(campaignId, eventType, recipientEmail) {
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  // Hash email to keep ID reasonable length
  const emailHash = recipientEmail
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .substring(0, 20);
  return `${campaignId}_${eventType}_${emailHash}_${timestamp}`;
}

/**
 * Generate ID for lead sources
 * @param {string} sourceName - Lead source name
 * @returns {string} Format: {sanitizedName}
 */
export function generateLeadSourceId(sourceName) {
  return sourceName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Generate ID for contact focus options
 * @param {string} focusName - Focus area name
 * @returns {string} Format: {sanitizedName}
 */
export function generateContactFocusId(focusName) {
  return focusName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Generate ID for notes
 * @param {string} entityType - Type of parent entity (customer, opportunity, task, contact)
 * @param {string} entityId - ID of parent entity
 * @returns {string} Format: {entityType}_{entityId}_note_{timestamp}
 */
export function generateNoteId(entityType, entityId) {
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  return `${entityType}_${entityId}_note_${timestamp}`;
}

/**
 * Generate ID for attachments
 * @param {string} entityType - Type of parent entity (customer, opportunity, task, etc.)
 * @param {string} entityId - ID of parent entity
 * @param {string} filename - Original filename
 * @returns {string} Format: {entityType}_{entityId}_{sanitizedFilename}_{timestamp}
 */
export function generateAttachmentId(entityType, entityId, filename) {
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  const sanitizedFilename = filename
    .toLowerCase()
    .replace(/\.[^.]+$/, '') // Remove extension
    .replace(/[^a-z0-9]+/g, '_')
    .substring(0, 30);
  return `${entityType}_${entityId}_${sanitizedFilename}_${timestamp}`;
}

/**
 * Generic timestamp-based ID generator
 * @param {string} prefix - Prefix for the ID
 * @returns {string} Format: {prefix}_{timestamp}
 */
export function generateTimestampId(prefix = '') {
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  return prefix ? `${prefix}_${timestamp}` : timestamp;
}

/**
 * Generate ID for security log documents
 * @param {string} action - Security action (login, logout, login_failed, password_reset, entity_deleted)
 * @param {string} userId - User ID performing or affected by the action
 * @returns {string} Format: security_{action}_{userId}_{timestamp}
 */
export function generateSecurityLogId(action, userId = 'unknown') {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  const ms = String(now.getMilliseconds()).padStart(3, '0');
  const rand = Math.random().toString(36).substring(2, 6);
  const sanitizedAction = action.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  const sanitizedUserId = userId.replace(/[^a-zA-Z0-9]+/g, '_').substring(0, 30);
  return `security_${sanitizedAction}_${sanitizedUserId}_${timestamp}_${ms}_${rand}`;
}

/**
 * Security action types enum
 */
export const SECURITY_ACTIONS = {
  LOGIN: 'login',
  LOGOUT: 'logout',
  LOGIN_FAILED: 'login_failed',
  PASSWORD_RESET_REQUEST: 'password_reset_request',
  PASSWORD_RESET_SUCCESS: 'password_reset_success',
  ENTITY_DELETED: 'entity_deleted',
  PERMISSION_DENIED: 'permission_denied',
  SESSION_EXPIRED: 'session_expired'
};

/**
 * Sanitize any string for use as document ID
 * @param {string} str - String to sanitize
 * @param {number} maxLength - Maximum length (default 100)
 * @returns {string} Sanitized string safe for Firestore document IDs
 */
export function sanitizeDocumentId(str, maxLength = 100) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, maxLength);
}

/**
 * Generate ID for Knowledge Base articles
 * @param {string} title - Article title
 * @param {string} source - Source type ('manual', 'ai-feedback', 'faq')
 * @returns {string} Format: kb_{source}_{sanitized_title}_{timestamp}
 */
export function generateKBArticleId(title, source = 'manual') {
  const sanitized = sanitizeDocumentId(title, 40);
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15);
  return `kb_${source}_${sanitized}_${timestamp}`;
}

/**
 * Generate ID for FAQ documents
 * @param {string} question - FAQ question text
 * @returns {string} Format: faq_{sanitized_question}_{timestamp}
 */
export function generateFaqId(question) {
  const sanitized = sanitizeDocumentId(question, 50);
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15); // YYYYMMDDTHHMMSS
  return `faq_${sanitized}_${timestamp}`;
}

/**
 * Generate ID for Support Page Section documents
 * Section IDs are manually defined and should be human-readable slugs (no timestamp)
 * @param {string} sectionName - Section name or provided ID
 * @returns {string} Format: {sanitized_name} (e.g., "getting-started", "customers")
 */
export function generateSectionId(sectionName) {
  return sectionName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50);
}

/**
 * Generate ID for points log entries
 * @param {string} userId - User ID who earned points
 * @param {string} action - Action that triggered points (e.g., OPPORTUNITY_WON)
 * @returns {string} Format: points_{userId}_{action}_{timestamp}
 */
export function generatePointsLogId(userId, action) {
  const sanitizedAction = action.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15);
  return `points_${userId}_${sanitizedAction}_${timestamp}`;
}

/**
 * Generate ID for chatbot usage logs
 * @param {string} userId - User ID who used chatbot
 * @param {string} [userEmail] - Optional user email for human-readable ID
 * @returns {string} Format: chatbot_{username}_{timestamp}
 * @example generateChatbotUsageId('abc123', 'john.doe@company.com') → 'chatbot_john_doe_20260122T143025'
 */
export function generateChatbotUsageId(userId, userEmail) {
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15);
  // Extract username from email for human-readable ID (e.g., john.doe@company.com -> john_doe)
  const sanitizedUsername = userEmail 
    ? userEmail.split('@')[0].replace(/[^a-zA-Z0-9]/g, '_').toLowerCase() 
    : userId;
  return `chatbot_${sanitizedUsername}_${timestamp}`;
}
